import { formatDateLong, type AnnualWheelThemeDto, type BriefDto } from '@nova/shared';
import { Sparkles } from 'lucide-react';
import Markdown from 'react-markdown';
import { GlassPanel } from '../../components/GlassPanel.js';
import { NovaOrb, type SystemStatus } from '../../components/NovaOrb.js';
import { Skeleton } from '../../components/Skeleton.js';

const MONTHS = [
  'januar',
  'februar',
  'mars',
  'april',
  'mai',
  'juni',
  'juli',
  'august',
  'september',
  'oktober',
  'november',
  'desember',
];

/**
 * «Ukens driv» sits at the top of the dashboard: Nova speaking in first person,
 * a weekly, motivating brief that stays up through the week — the week's wins,
 * the team's progress and one opportunity worth grabbing (06-designsystem.md).
 */
export function BriefPanel({
  brief,
  theme,
  status,
  loading,
}: {
  brief: BriefDto | null;
  theme: AnnualWheelThemeDto | null;
  status: SystemStatus;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <GlassPanel className="space-y-3">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-5 w-3/5" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-4/5" />
      </GlassPanel>
    );
  }

  return (
    <GlassPanel className="relative" data-testid="brief">
      {/* A faint cyan wash behind the orb makes the panel read as "Nova speaking". */}
      <div
        aria-hidden
        className="bg-accent/10 pointer-events-none absolute -left-20 -top-24 h-56 w-56 rounded-full blur-3xl"
      />

      <div className="relative flex gap-4">
        <div className="mt-1 shrink-0">
          <NovaOrb status={status} size={14} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="label-micro">Nova · Ukens driv</p>
            <span className="text-ink-soft text-[11px]">
              {formatDateLong(brief?.date ?? new Date())}
            </span>
            {theme && (
              <span className="border-violet/25 bg-violet/10 text-violet inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]">
                <Sparkles size={11} aria-hidden />
                Årshjul: {theme.theme}
              </span>
            )}
          </div>

          {brief ? (
            <>
              <h1 className="font-display text-ink mt-2 text-xl font-medium leading-snug">
                {brief.headline}
              </h1>
              <div className="prose-nova text-ink-soft mt-3 space-y-3 text-sm leading-relaxed">
                <Markdown
                  components={{
                    p: ({ children }) => <p>{children}</p>,
                    strong: ({ children }) => (
                      <strong className="text-ink font-semibold">{children}</strong>
                    ),
                  }}
                >
                  {brief.body}
                </Markdown>
              </div>
            </>
          ) : (
            <p className="text-ink-soft mt-2 text-sm">
              Jeg har ikke skrevet «Ukens driv» ennå. Den kommer når Markedsdirektøren kjører mandag
              morgen — eller du kan starte den selv fra «Kjør agent».
            </p>
          )}
        </div>
      </div>
    </GlassPanel>
  );
}

export function currentMonthLabel(month: number): string {
  return MONTHS[month - 1] ?? '';
}
